import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

// Steel & Ember — Button vocabulary
// `default` = Steel primary (the in-app workhorse).
// `ember`   = Construction Orange — RARE. Reserved for Landing CTAs, trial sign-up,
//             and irreversible destructive-yet-positive actions.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:     "bg-steel-500 text-white shadow-sm hover:bg-steel-600 active:bg-steel-700",
        ember:       "bg-ember-500 text-white shadow-sm shadow-ember-500/20 hover:bg-ember-600 active:bg-ember-700",
        destructive: "bg-crimson-500 text-white shadow-sm hover:bg-crimson-600",
        outline:     "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:   "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost:       "hover:bg-accent hover:text-accent-foreground",
        link:        "text-steel-500 underline-offset-4 hover:underline dark:text-steel-300",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm:      "h-8 rounded-md px-3 text-xs",
        lg:      "h-10 rounded-md px-8",
        xl:      "h-12 rounded-md px-10 text-[13px] tracking-[0.06em]",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
